import { CommonModule, DOCUMENT } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, DestroyRef, HostListener, ViewChild, computed, effect, inject, signal, untracked } from "@angular/core";
import { FormsModule, Validators } from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute, ParamMap, Params, Router } from "@angular/router";
import { EzFormComponent } from "@template-ui/components/ez-form/ez-form.component";
import { UiLibraryModule } from "@template-ui/template-ui.module";
import { EzFormInputType, InputConfig } from "@template-ui/types/ez-form.types";
import { ButtonConfig, EzGridDsConfig, PanelHeaderConfig } from "@template-ui/types";
import { ColDef } from "ag-grid-community";
import { ButtonCellRenderer } from "@app/shared/components/tables/ag-grid-cells/button-cell-renderer/button-cell-renderer.component";
import { RoleService } from "@app/shared/services/roles.service";
import { TerminologyEditorStateService } from "./terminology-editor-state.service";
import {
    normalizeTerminologyContextValue,
    normalizeTerminologyReturnUrl,
    normalizeTerminologyReturnUsage,
    restoreTerminologyUsageFocus,
} from "@app/shared/terminology/terminology-contextual-navigation";
import {
    findTerminologyUsages,
    mapTerminologyAdminError,
    TerminologyAdminDisplayError,
    TerminologyEntryCreateRequest,
    TerminologyEntryDetail,
    TerminologyEntryFormValues,
    TerminologyEntryUpdateRequest,
    TerminologyUsageManifest,
    TerminologyVocabularyCatalog,
    TerminologyVocabularyCatalogItem,
    TerminologyVocabularyDefinitionCreateRequest,
    TerminologyVocabularyDefinitionDetail,
    TerminologyVocabularyPublicationDetail,
} from "./platform-terminology-admin.models";
import { PlatformTerminologyAdminService } from "./platform-terminology-admin.service";
import {
    PlatformTerminologyVocabularyEditorComponent,
    TerminologyEditorWorkspaceView,
} from "./platform-terminology-vocabulary-editor.component";
import { TerminologyConceptCatalogComponent, TerminologyConceptCatalogViewState } from "./terminology-concept-catalog.component";

const TERMINOLOGY_READ = "terminology.read";
const TERMINOLOGY_AUTHOR = "terminology.author";
const TERMINOLOGY_PREVIEW = "terminology.preview";
const TERMINOLOGY_PUBLISH = "terminology.publish";
const TERMINOLOGY_RETIRE = "terminology.retire";
const TERMINOLOGY_RESTORE = "terminology.restore";
const CATALOG_PAGE_SIZE = 50;
const DEFINITION_TOKEN_PATTERN = /^[a-z0-9][a-z0-9._-]*$/;

type TerminologyWorkspaceMode = "catalog" | "legacy-editor";

interface TerminologyContextualSource {
    readonly returnUrl: string | null;
    readonly returnUsageId: string | null;
    readonly localeCode: string | null;
    readonly jurisdictionCode: string | null;
    readonly programCode: string | null;
    readonly channelCode: string | null;
    readonly audienceCode: string | null;
    readonly mapRevision: string | null;
}

@Component({
    selector: "phms-platform-terminology-admin",
    imports: [CommonModule, FormsModule, UiLibraryModule, PlatformTerminologyVocabularyEditorComponent, TerminologyConceptCatalogComponent],
    templateUrl: "./platform-terminology-admin.component.html",
    styleUrl: "./platform-terminology-admin.component.scss"
})
export class PlatformTerminologyAdminComponent {
    private readonly destroyRef = inject(DestroyRef);
    private readonly terminologyAdminService = inject(PlatformTerminologyAdminService);
    private readonly roles = inject(RoleService);
    private readonly editorState = inject(TerminologyEditorStateService);
    private readonly document = inject(DOCUMENT);
    private readonly route = inject(ActivatedRoute, { optional: true });
    private readonly router = inject(Router, { optional: true });
    private vocabularyCatalogRequestId = 0;
    private definitionDetailRequestId = 0;
    private creationRequestId = 0;
    private pendingInlineDefinitionId: number | null = null;
    private lastEditorParameters: Params | null = null;
    private lastEditorFragment: string | null = null;
    private approvedCatalogQuery: string | null = null;
    private previousEditorAuthority: string | null = null;
    private childEditingState = { dirty: false, busy: false };

    @ViewChild("terminologyForm")
    private terminologyForm?: EzFormComponent;
    @ViewChild(PlatformTerminologyVocabularyEditorComponent)
    private vocabularyEditor?: PlatformTerminologyVocabularyEditorComponent;

    readonly canRead = this.roles.hasAnyPermissionSignal(TERMINOLOGY_READ);
    readonly canAuthor = this.roles.hasAnyPermissionSignal(TERMINOLOGY_AUTHOR);
    readonly canPreview = this.roles.hasAnyPermissionSignal(TERMINOLOGY_PREVIEW);
    readonly canPublish = this.roles.hasAnyPermissionSignal(TERMINOLOGY_PUBLISH);
    readonly canRetire = this.roles.hasAnyPermissionSignal(TERMINOLOGY_RETIRE);
    readonly canRestore = this.roles.hasAnyPermissionSignal(TERMINOLOGY_RESTORE);

    readonly isEntryLoading = signal(false);
    readonly isSaving = signal(false);
    readonly activeActionKey = signal<string | null>(null);
    readonly isCreatingEntry = signal(false);
    readonly catalogReloadTrigger = signal(0);
    readonly selectedEntry = signal<TerminologyEntryDetail | null>(null);
    readonly successMessage = signal<string | null>(null);
    readonly errorState = signal<TerminologyAdminDisplayError | null>(null);
    readonly usageManifest = signal<TerminologyUsageManifest | null>(null);
    readonly isUsageManifestLoading = signal(false);
    readonly usageManifestError = signal<string | null>(null);
    readonly workspaceMode = signal<TerminologyWorkspaceMode>("catalog");
    readonly showConceptCatalog = signal(true);
    readonly showEditorCatalog = signal(false);
    conceptCatalogState: TerminologyConceptCatalogViewState | null = null;
    readonly editorAuthorityKey = computed(() => {
        const profile = this.roles.accessProfile();
        return JSON.stringify([profile?.actor.actorId ?? "", profile?.permissions ?? [], profile?.scopes ?? [], this.canRead(), this.canAuthor()]);
    });
    readonly editorContextKey = computed(() => JSON.stringify([
        this.editorAuthorityKey(), this.vocabularyConfigurationId(this.selectedDefinitionDetail()),
    ]));
    private readonly editorAuthorityEffect = effect(() => {
        const key = this.editorAuthorityKey();
        untracked(() => {
            if (this.previousEditorAuthority !== null && key !== this.previousEditorAuthority) {
                ++this.vocabularyCatalogRequestId;
                ++this.definitionDetailRequestId;
                this.selectedDefinitionDetail.set(null);
                this.vocabularyCatalog.set(null);
                this.isDefinitionDetailLoading.set(false);
                this.isVocabularyCatalogLoading.set(false);
                this.resetVocabularyDefinitionCreateState();
                this.childEditingState = { dirty: false, busy: false };
                this.editorState.clear();
                this.showConceptCatalog.set(true);
                this.conceptCatalogState = null;
            }
            this.previousEditorAuthority = key;
        });
    });
    private readonly creationNavigationEffect = effect(() => {
        this.isCreatingVocabularyDefinition();
        this.isCreatingVocabularyDefinitionSaving();
        untracked(() => this.syncEditorNavigationState());
    });
    readonly vocabularyCatalog = signal<TerminologyVocabularyCatalog | null>(null);
    readonly selectedDefinitionDetail = signal<TerminologyVocabularyDefinitionDetail | null>(null);
    readonly isVocabularyCatalogLoading = signal(false);
    readonly isDefinitionDetailLoading = signal(false);
    readonly vocabularyCatalogError = signal<TerminologyAdminDisplayError | null>(null);
    readonly vocabularyCatalogSuccess = signal<string | null>(null);
    readonly isCreatingVocabularyDefinition = signal(false);
    readonly isCreatingVocabularyDefinitionSaving = signal(false);
    readonly newDefinitionFieldErrors = signal<Record<string, string>>({});
    readonly tokenCopied = signal(false);
    readonly selectedUsageId = signal<string | null>(null);
    readonly selectedVocabularyView = signal<TerminologyEditorWorkspaceView>("edit");
    readonly contextualSource = signal<TerminologyContextualSource | null>(null);

    catalogSearch = "";
    catalogKindCode = "";
    catalogStatusCode = "";
    catalogUsageStateCode = "";
    catalogPage = 1;
    newDefinitionTokenCode = "";
    newDefinitionContentText = "";
    newDefinitionDescription = "";

    readonly selectedDefinitionId = computed(() => this.selectedDefinitionDetail()?.definition.definitionId ?? null);
    readonly catalogTotalPages = computed(() => {
        const totalCount = this.vocabularyCatalog()?.totalCount ?? 0;
        return Math.max(1, Math.ceil(totalCount / CATALOG_PAGE_SIZE));
    });
    readonly catalogResultRange = computed(() => {
        const catalog = this.vocabularyCatalog();
        if (!catalog || catalog.totalCount === 0) {
            return "0 definitions";
        }

        const first = catalog.skip + 1;
        const last = Math.min(catalog.skip + catalog.items.length, catalog.totalCount);
        return `${first}-${last} of ${catalog.totalCount}`;
    });
    readonly contextualSourceLabel = computed(() => {
        const source = this.contextualSource();
        if (!source) {
            return "";
        }

        const usageId = source.returnUsageId ?? this.selectedUsageId();
        const usage = this.selectedDefinitionDetail()?.usages.find(candidate => candidate.usageId === usageId);
        return usage
            ? `${this.toDisplayLabel(usage.surfaceCode)} · ${usage.componentCode}`
            : usageId
                ? `Usage ${usageId}`
                : "Application wording";
    });
    readonly contextualSourceScopeLabel = computed(() => {
        const source = this.contextualSource();
        if (!source) {
            return "";
        }

        const parts = [
            source.jurisdictionCode?.toUpperCase(),
            source.programCode?.toUpperCase(),
            source.localeCode,
            source.channelCode ? this.toDisplayLabel(source.channelCode) : null,
            source.audienceCode ? this.toDisplayLabel(source.audienceCode) : null,
        ].filter((value): value is string => !!value);
        return parts.length > 0 ? parts.join(" / ") : "Authorized runtime context";
    });
    readonly contextualLoadedWordingLabel = computed(() => {
        const definition = this.selectedDefinitionDetail()?.definition;
        if (!definition) {
            return "Resolving current wording…";
        }

        const revision = definition.activeRevisionId ? `rev-${definition.activeRevisionId}` : "no active revision";
        return `${definition.versionCode || "Current program pack"} / ${revision} (catalog)`;
    });
    readonly contextualInfluenceLabel = computed(() => {
        const detail = this.selectedDefinitionDetail();
        if (!detail) {
            return "Resolving definition relationships…";
        }

        const definition = detail.definition;
        const kindCode = normalizeCode(definition.kindCode);
        if (kindCode === "concept" || kindCode === "form" || kindCode === "concept-form") {
            const dependentCount = detail.dependentDefinitions.length;
            return dependentCount > 0
                ? `Shared ${this.toDisplayLabel(definition.grammaticalFormCode || definition.kindCode)} · affects ${dependentCount} linked ${dependentCount === 1 ? "phrase" : "phrases"}`
                : `Shared ${this.toDisplayLabel(definition.grammaticalFormCode || definition.kindCode)} · no linked phrases`;
        }

        return definition.currentReferenceTokenCodes.length > 0
            ? `Linked phrase · ${definition.currentReferenceTokenCodes.length} shared ${definition.currentReferenceTokenCodes.length === 1 ? "form" : "forms"}`
            : this.toDisplayLabel(definition.kindCode);
    });
    readonly contextualSnapshotLabel = computed(() => {
        const revision = this.contextualSource()?.mapRevision;
        return revision
            ? `Runtime snapshot ${revision.length > 16 ? `${revision.slice(0, 16)}…` : revision}`
            : "Runtime snapshot unavailable";
    });

    readonly catalogColumnDefs: ColDef[] = this.buildCatalogColumnDefs();
    readonly catalogGridConfig: EzGridDsConfig = this.buildCatalogGridConfig();
    readonly catalogPanelHeaderConfig: PanelHeaderConfig = this.buildCatalogPanelHeaderConfig();
    readonly pagePanelSubtitle = "Manage customer-controlled wording and review exactly where each terminology value appears.";
    readonly pagePanelHeaderConfig: PanelHeaderConfig = {
        additionalActions: [
            {
                name: "refreshCatalog",
                type: EzFormInputType.BUTTON,
                buttonText: "Refresh",
                disabled: this.isEntryLoading,
            },
            {
                name: "beginCreateEntry",
                type: EzFormInputType.BUTTON,
                buttonText: "New Entry",
                disabled: () => !this.canAuthor() || this.isSaving(),
            },
        ],
        additionalActionsContainerClass: "gap-2 items-center compact",
    };

    readonly selectedEntryId = computed(() => this.selectedEntry()?.id ?? null);
    readonly selectedEntryUsages = computed(() => {
        const entry = this.selectedEntry();
        return findTerminologyUsages(this.usageManifest(), entry?.configurationCode, entry?.tokenCode);
    });
    readonly selectedEntryStatusLabel = computed(() => this.toStatusLabel(this.selectedEntry()?.statusCode));
    readonly editBlockedReason = computed(() => {
        const entry = this.selectedEntry();
        if (this.isCreatingEntry()) {
            return null;
        }

        if (!entry) {
            return "Select a terminology entry before editing it.";
        }

        switch (normalizeCode(entry.statusCode)) {
            case "published":
                return "Published terminology entries must be rolled back before they can be edited.";
            case "retired":
                return "Retired terminology entries must be rolled back before they can be edited.";
            default:
                return null;
        }
    });
    readonly entryFormActionButtons = computed<ButtonConfig[]>(() => [
        {
            id: "saveEntry",
            buttonText: this.isSaving() ? "Saving..." : (this.isCreatingEntry() ? "Create Entry" : "Save Entry"),
            disabled: !this.canAuthor() || this.isSaving() || !!this.editBlockedReason(),
        },
        {
            id: "resetForm",
            buttonText: "Reset",
            severity: "secondary",
            disabled: this.isSaving(),
        },
        {
            id: "publishSelectedEntry",
            buttonText: this.isActionBusy("publish") ? "Publishing..." : "Publish",
            severity: "secondary",
            disabled: !this.canPublish() || !this.canPublishSelectedEntry() || this.isActionBusy("publish") || this.selectedEntryId() === null,
        },
        {
            id: "retireSelectedEntry",
            buttonText: this.isActionBusy("retire") ? "Retiring..." : "Retire",
            severity: "danger",
            disabled: !this.canRetire() || !this.canRetireSelectedEntry() || this.isActionBusy("retire") || this.selectedEntryId() === null,
        },
        {
            id: "rollbackSelectedEntry",
            buttonText: this.isActionBusy("rollback") ? "Rolling Back..." : "Rollback",
            severity: "secondary",
            disabled: !this.canRestore() || !this.canRollbackSelectedEntry() || this.isActionBusy("rollback") || this.selectedEntryId() === null,
        },
    ]);

    terminologyFormConfig: InputConfig[] = this.buildTerminologyFormConfig(true);
    formValues: TerminologyEntryFormValues = this.createEmptyFormValues();

    constructor() {
        this.destroyRef.onDestroy(() => {
            ++this.creationRequestId;
            this.editorState.clear();
        });
        this.loadUsageManifest();
        this.initializeCatalogNavigation();
    }

    @HostListener("window:beforeunload", ["$event"])
    protectTerminologyEdits(event: BeforeUnloadEvent): void {
        this.syncEditorNavigationState();
        if (!this.editorState.isNavigationBlocked()) return;
        event.preventDefault();
        event.returnValue = "";
    }

    onEditorEditingStateChange(state: { dirty: boolean; busy: boolean }): void {
        this.childEditingState = state;
        this.syncEditorNavigationState();
    }

    editorNavigationBusy(): boolean { return this.editorState.busy(); }

    onTerminologyAccessLost(): void {
        ++this.vocabularyCatalogRequestId;
        ++this.definitionDetailRequestId;
        this.selectedDefinitionDetail.set(null);
        this.vocabularyCatalog.set(null);
        this.isDefinitionDetailLoading.set(false);
        this.isVocabularyCatalogLoading.set(false);
        this.childEditingState = { dirty: false, busy: false };
        this.resetVocabularyDefinitionCreateState();
        this.editorState.clear();
        this.showConceptCatalog.set(true);
        this.conceptCatalogState = null;
    }

    syncEditorNavigationState(): void {
        this.editorState.update({
            dirty: this.childEditingState.dirty || (this.isCreatingVocabularyDefinition()
                && [this.newDefinitionTokenCode, this.newDefinitionContentText, this.newDefinitionDescription].some(value => value.length > 0)),
            busy: this.childEditingState.busy || this.isCreatingVocabularyDefinitionSaving(),
        });
    }

    private clearChildEditingState(): void {
        this.childEditingState = { dirty: false, busy: false };
        this.syncEditorNavigationState();
    }

    onPagePanelHeaderButtonClick(event: { name?: string }): void {
        switch (event.name) {
            case "refreshCatalog":
                this.refreshCatalog();
                break;
            case "beginCreateEntry":
                this.beginCreateEntry();
                break;
            default:
                break;
        }
    }

    onEntryFormActionButtonClick(button: ButtonConfig): void {
        switch (button.id) {
            case "saveEntry":
                this.saveEntry();
                break;
            case "resetForm":
                this.resetForm();
                break;
            case "publishSelectedEntry":
                this.publishSelectedEntry();
                break;
            case "retireSelectedEntry":
                this.retireSelectedEntry();
                break;
            case "rollbackSelectedEntry":
                this.rollbackSelectedEntry();
                break;
            default:
                break;
        }
    }

    beginCreateEntry(clearMessages = true): void {
        if (clearMessages) {
            this.clearMessages();
        }

        this.isCreatingEntry.set(true);
        this.selectedEntry.set(null);
        this.terminologyFormConfig = this.buildTerminologyFormConfig(true);
        this.formValues = this.createEmptyFormValues();
        this.terminologyForm?.clearErrors();
    }

    selectEntry(entryId: number, clearMessages = true): void {
        if (clearMessages) {
            this.clearMessages();
        }

        this.isCreatingEntry.set(false);
        this.isEntryLoading.set(true);

        this.terminologyAdminService
            .getEntry(entryId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (entry) => {
                    this.selectedEntry.set(entry);
                    this.terminologyFormConfig = this.buildTerminologyFormConfig(false);
                    this.formValues = this.mapEntryToFormValues(entry);
                    this.isEntryLoading.set(false);
                    this.terminologyForm?.clearErrors();
                },
                error: (error) => {
                    this.isEntryLoading.set(false);
                    this.errorState.set(mapTerminologyAdminError(error));
                },
            });
    }

    saveEntry(): void {
        if (!this.canAuthor()) {
            this.errorState.set({
                title: "Access denied",
                messages: ["You do not have permission to author terminology entries."],
                issueCodes: [],
            });
            return;
        }

        const blockedReason = this.editBlockedReason();
        if (blockedReason) {
            this.errorState.set({
                title: "Entry is read-only",
                messages: [blockedReason],
                issueCodes: [],
            });
            return;
        }

        this.clearMessages();
        if (!this.ensureFormIsValid(this.terminologyForm, "Resolve the terminology form errors before saving.")) {
            return;
        }

        const normalizedDates = this.normalizeEntryDates(this.formValues);
        const validationMessages = this.validateFormValues(
            this.formValues,
            normalizedDates.effectiveStartUtc,
            normalizedDates.effectiveEndUtc,
        );
        if (validationMessages.length > 0) {
            this.errorState.set({
                title: "Validation failed",
                messages: validationMessages,
                issueCodes: [],
            });
            return;
        }

        const effectiveStartUtc = normalizedDates.effectiveStartUtc;
        if (!effectiveStartUtc) {
            this.errorState.set({
                title: "Validation failed",
                messages: ["Effective start must be a valid date and time."],
                issueCodes: [],
            });
            return;
        }

        this.isSaving.set(true);

        const request$ = this.isCreatingEntry()
            ? this.terminologyAdminService.createEntry(this.buildCreateRequest(this.formValues, effectiveStartUtc, normalizedDates.effectiveEndUtc))
            : this.terminologyAdminService.updateEntry(this.selectedEntryId()!, this.buildUpdateRequest(this.formValues, effectiveStartUtc, normalizedDates.effectiveEndUtc));

        request$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (entry) => {
                    const wasCreatingEntry = this.isCreatingEntry();
                    this.isSaving.set(false);
                    this.selectedEntry.set(entry);
                    this.isCreatingEntry.set(false);
                    this.terminologyFormConfig = this.buildTerminologyFormConfig(false);
                    this.formValues = this.mapEntryToFormValues(entry);
                    this.successMessage.set(wasCreatingEntry ? "Terminology entry created successfully." : "Terminology entry saved successfully.");
                    this.catalogReloadTrigger.update(n => n + 1);
                },
                error: (error) => {
                    this.isSaving.set(false);
                    this.errorState.set(mapTerminologyAdminError(error));
                },
            });
    }

    publishSelectedEntry(): void {
        const entryId = this.selectedEntryId();
        if (entryId === null) {
            return;
        }

        this.clearMessages();
        this.activeActionKey.set("publish");
        this.terminologyAdminService
            .publishEntry(entryId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (entry) => {
                    this.activeActionKey.set(null);
                    this.selectedEntry.set(entry);
                    this.formValues = this.mapEntryToFormValues(entry);
                    this.successMessage.set("Terminology entry published successfully.");
                    this.catalogReloadTrigger.update(n => n + 1);
                },
                error: (error) => {
                    this.activeActionKey.set(null);
                    this.errorState.set(mapTerminologyAdminError(error));
                },
            });
    }

    retireSelectedEntry(): void {
        const entryId = this.selectedEntryId();
        if (entryId === null) {
            return;
        }

        this.clearMessages();
        this.activeActionKey.set("retire");
        this.terminologyAdminService
            .retireEntry(entryId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (entry) => {
                    this.activeActionKey.set(null);
                    this.selectedEntry.set(entry);
                    this.formValues = this.mapEntryToFormValues(entry);
                    this.successMessage.set("Terminology entry retired successfully.");
                    this.catalogReloadTrigger.update(n => n + 1);
                },
                error: (error) => {
                    this.activeActionKey.set(null);
                    this.errorState.set(mapTerminologyAdminError(error));
                },
            });
    }

    rollbackSelectedEntry(): void {
        const entryId = this.selectedEntryId();
        if (entryId === null) {
            return;
        }

        this.clearMessages();
        this.activeActionKey.set("rollback");
        this.terminologyAdminService
            .rollbackEntry(entryId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (entry) => {
                    this.activeActionKey.set(null);
                    this.selectedEntry.set(entry);
                    this.formValues = this.mapEntryToFormValues(entry);
                    this.successMessage.set("Terminology entry rolled back successfully.");
                    this.catalogReloadTrigger.update(n => n + 1);
                },
                error: (error) => {
                    this.activeActionKey.set(null);
                    this.errorState.set(mapTerminologyAdminError(error));
                },
            });
    }

    resetForm(): void {
        this.terminologyForm?.clearErrors();
        this.errorState.set(null);

        if (this.isCreatingEntry()) {
            this.formValues = this.createEmptyFormValues();
            return;
        }

        const entry = this.selectedEntry();
        if (entry) {
            this.formValues = this.mapEntryToFormValues(entry);
        }
    }

    isActionBusy(actionKey: string): boolean {
        return this.activeActionKey() === actionKey;
    }

    canPublishSelectedEntry(): boolean {
        return normalizeCode(this.selectedEntry()?.statusCode) !== "published";
    }

    canRetireSelectedEntry(): boolean {
        return this.selectedEntryId() !== null && normalizeCode(this.selectedEntry()?.statusCode) !== "retired";
    }

    canRollbackSelectedEntry(): boolean {
        const status = normalizeCode(this.selectedEntry()?.statusCode);
        return status === "published" || status === "retired";
    }

    applyCatalogFilters(confirmDiscard = true): void {
        if (confirmDiscard && !this.confirmDiscardVocabularyChanges()) {
            return;
        }
        this.resetVocabularyDefinitionCreateState();

        this.updateCatalogQuery({
            mode: "vocabulary-editor",
            q: this.catalogSearch.trim() || null,
            kind: this.catalogKindCode || null,
            status: this.catalogStatusCode || null,
            usageState: this.catalogUsageStateCode || null,
            page: null,
            definition: null,
            token: null,
            configuration: null,
            usage: null,
        });
    }

    clearCatalogFilters(): void {
        if (!this.confirmDiscardVocabularyChanges()) {
            return;
        }

        this.catalogSearch = "";
        this.catalogKindCode = "";
        this.catalogStatusCode = "";
        this.catalogUsageStateCode = "";
        this.catalogPage = 1;
        this.applyCatalogFilters(false);
    }

    selectVocabularyDefinition(item: TerminologyVocabularyCatalogItem): void {
        if (item.definitionId !== this.selectedDefinitionId()
            && !this.confirmDiscardVocabularyChanges()) {
            return;
        }

        this.resetVocabularyDefinitionCreateState();
        this.pendingInlineDefinitionId = item.definitionId;
        this.loadVocabularyDefinition(item.definitionId);
        this.updateCatalogQuery({
            mode: null,
            definition: item.definitionId,
            token: null,
            configuration: null,
            usage: null,
            view: "edit",
        });
    }

    goToCatalogPage(page: number): void {
        const targetPage = Math.min(Math.max(1, page), this.catalogTotalPages());
        if (targetPage === this.catalogPage) {
            return;
        }
        if (!this.confirmDiscardVocabularyChanges()) {
            return;
        }

        this.resetVocabularyDefinitionCreateState();
        this.updateCatalogQuery({
            mode: "vocabulary-editor",
            page: targetPage === 1 ? null : targetPage,
            definition: null,
            token: null,
            configuration: null,
            usage: null,
        });
    }

    refreshVocabularyCatalog(): void {
        if (this.editorState.busy()) return;
        const selectedId = this.selectedDefinitionDetail()?.definition.definitionId;
        if (selectedId && this.vocabularyEditor?.hasUnsavedChanges) {
            this.vocabularyEditor.reloadCurrentWording();
            return;
        }
        this.loadVocabularyCatalog(
            selectedId ?? this.readPositiveInteger(this.route?.snapshot.queryParamMap.get("definition") ?? null),
            this.route?.snapshot.queryParamMap.get("token") ?? null,
            this.route?.snapshot.queryParamMap.get("configuration") ?? null,
        );
    }

    editCurrentPackDefinition(definitionId: number): void {
        this.showConceptCatalog.set(false);
        this.openVocabularyEditorDefinition(definitionId);
    }

    createCurrentPackDefinition(): void {
        if (!this.confirmDiscardVocabularyChanges()) return;
        this.showConceptCatalog.set(false);
        this.updateCatalogQuery({ mode: "vocabulary-editor", definition: null, token: null, configuration: null, usage: null });
        this.beginCreateFromCatalog(true);
    }

    browseConceptCatalog(): void {
        if (!this.confirmDiscardVocabularyChanges()) return;
        this.clearChildEditingState();
        this.resetVocabularyDefinitionCreateState();
        this.selectedDefinitionDetail.set(null);
        this.selectedEntry.set(null);
        ++this.definitionDetailRequestId;
        ++this.vocabularyCatalogRequestId;
        this.showConceptCatalog.set(true);
        if (this.router && this.route) {
            this.updateCatalogQuery({ mode: null, definition: null, token: null, configuration: null, usage: null, entry: null });
        }
    }

    openVocabularyEditorDefinition(definitionId: number, confirmed = false): void {
        if (this.editorState.busy() || (!confirmed && !this.confirmDiscardVocabularyChanges())) return;
        this.pendingInlineDefinitionId = definitionId;
        this.loadVocabularyDefinition(definitionId);
        this.updateCatalogQuery({
            mode: null,
            definition: definitionId,
            token: null,
            configuration: null,
            usage: null,
            view: "edit",
            entry: null,
        });
    }

    reloadVocabularyEditorDefinition(definitionId: number): void {
        if (this.selectedDefinitionDetail()?.definition.definitionId !== definitionId) return;
        this.loadVocabularyDefinition(definitionId);
    }

    onVocabularyWordingSaved(publication: TerminologyVocabularyPublicationDetail): void {
        if (this.selectedDefinitionDetail()?.definition.definitionId !== publication.definitionId) return;
        this.loadVocabularyDefinition(publication.definitionId);
    }

    beginCreateFromCatalog(confirmed = false): void {
        if (!this.canAuthor()) {
            this.vocabularyCatalogError.set({
                title: "Access denied",
                messages: ["You do not have permission to author terminology entries."],
                issueCodes: [],
            });
            return;
        }
        if (!confirmed && !this.confirmDiscardVocabularyChanges()) {
            return;
        }

        this.clearChildEditingState();
        this.vocabularyCatalogError.set(null);
        this.vocabularyCatalogSuccess.set(null);
        this.newDefinitionTokenCode = "";
        this.newDefinitionContentText = "";
        this.newDefinitionDescription = "";
        this.newDefinitionFieldErrors.set({});
        this.isCreatingVocabularyDefinition.set(true);
    }

    cancelCreateVocabularyDefinition(): void {
        if (!this.confirmDiscardVocabularyChanges()) {
            return;
        }

        this.resetVocabularyDefinitionCreateState();
        this.vocabularyCatalogError.set(null);
    }

    saveNewVocabularyDefinition(): void {
        if (this.isCreatingVocabularyDefinitionSaving()) {
            return;
        }

        const tokenCode = this.newDefinitionTokenCode.trim().toLowerCase();
        const contentText = this.newDefinitionContentText.trim();
        const description = this.newDefinitionDescription.trim();
        const fieldErrors: Record<string, string> = {};
        if (!DEFINITION_TOKEN_PATTERN.test(tokenCode) || tokenCode.length > 160) {
            fieldErrors["newDefinitionTokenCode"] = "Token must start with a letter or number and use only lowercase letters, numbers, periods, underscores, or hyphens.";
        }
        if (!contentText || contentText.length > 2000) {
            fieldErrors["newDefinitionContentText"] = "Initial wording is required and cannot exceed 2,000 characters.";
        }
        if (description.length > 1000) {
            fieldErrors["newDefinitionDescription"] = "Description cannot exceed 1,000 characters.";
        }
        this.newDefinitionFieldErrors.set(fieldErrors);
        const validationMessages = Object.values(fieldErrors);
        if (validationMessages.length > 0) {
            this.vocabularyCatalogError.set({
                title: "Check the new definition",
                messages: validationMessages,
                issueCodes: [],
            });
            const field = Object.keys(fieldErrors)[0], requestId = this.creationRequestId;
            this.document.defaultView?.setTimeout(() => {
                if (this.isCreatingVocabularyDefinition() && requestId === this.creationRequestId)
                    this.document.querySelector<HTMLElement>(`[data-testid="terminology-definition-create"] [name="${field}"]`)?.focus();
            });
            return;
        }

        const request: TerminologyVocabularyDefinitionCreateRequest = {
            tokenCode,
            contentText,
            description: description || null,
        };
        this.vocabularyCatalogError.set(null);
        this.vocabularyCatalogSuccess.set(null);
        this.isCreatingVocabularyDefinitionSaving.set(true);
        const requestId = ++this.creationRequestId;
        const authority = this.editorAuthorityKey();
        this.terminologyAdminService
            .createVocabularyDefinition(request)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (publication) => {
                    if (requestId !== this.creationRequestId || authority !== this.editorAuthorityKey() || !this.canRead() || !this.canAuthor()) return;
                    this.isCreatingVocabularyDefinitionSaving.set(false);
                    this.resetVocabularyDefinitionCreateState();
                    this.vocabularyCatalogSuccess.set(`Created ${publication.tokenCode}. Its wording is active now.`);
                    this.catalogSearch = "";
                    this.catalogKindCode = "";
                    this.catalogStatusCode = "";
                    this.catalogUsageStateCode = "";
                    this.catalogPage = 1;
                    this.updateCatalogQuery({
                        mode: null,
                        entry: null,
                        q: null,
                        kind: null,
                        status: null,
                        usageState: null,
                        page: null,
                        definition: publication.definitionId,
                        token: null,
                        configuration: null,
                        usage: null,
                        view: "edit",
                    });
                },
                error: (error) => {
                    if (requestId !== this.creationRequestId || authority !== this.editorAuthorityKey()) return;
                    this.isCreatingVocabularyDefinitionSaving.set(false);
                    this.vocabularyCatalogError.set(mapTerminologyAdminError(error));
                },
            });
    }

    creationFieldChanged(field: string): void {
        this.newDefinitionFieldErrors.update(errors => {
            const next = { ...errors }; delete next[field]; return next;
        });
        this.syncEditorNavigationState();
    }

    returnToVocabularyCatalog(): void {
        this.updateCatalogQuery({ mode: null, entry: null });
    }

    returnToContextualSource(): void {
        const source = this.contextualSource();
        const returnUrl = normalizeTerminologyReturnUrl(source?.returnUrl);
        if (!this.router || !returnUrl) {
            return;
        }

        const returnUsageId = source?.returnUsageId ?? null;
        void this.router.navigateByUrl(returnUrl).then((navigated) => {
            if (!navigated || !returnUsageId) {
                return;
            }

            this.document.defaultView?.setTimeout(() => {
                restoreTerminologyUsageFocus(this.document, returnUsageId);
            });
        });
    }

    async copySelectedToken(): Promise<void> {
        const tokenCode = this.selectedDefinitionDetail()?.definition.tokenCode;
        if (!tokenCode || typeof navigator === "undefined" || !navigator.clipboard) {
            return;
        }

        await navigator.clipboard.writeText(tokenCode);
        this.tokenCopied.set(true);
        window.setTimeout(() => this.tokenCopied.set(false), 1600);
    }

    vocabularyStatusClass(statusCode: string | null | undefined): string {
        const normalized = normalizeCode(statusCode);
        return ["published", "draft", "unpublished", "inactive"].includes(normalized)
            ? `status-badge--${normalized}`
            : "status-badge--unknown";
    }

    vocabularyScope(item: TerminologyVocabularyCatalogItem): string {
        const parts = [
            item.localeCode || "all locales",
            item.channelCode || "all channels",
            item.audienceCode || "all audiences",
            item.tenantId ? `tenant ${item.tenantId}` : "all tenants",
        ];
        return parts.join(" / ");
    }

    toStatusLabel(statusCode: string | null | undefined): string {
        const normalized = normalizeCode(statusCode);
        if (normalized.length === 0) {
            return "Unknown";
        }

        return normalized
            .split("_")
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(" ");
    }

    refreshCatalog(): void {
        this.catalogReloadTrigger.update(n => n + 1);
        this.loadUsageManifest();
        if (this.workspaceMode() === "catalog") {
            this.refreshVocabularyCatalog();
        }
    }

    toDisplayLabel(value: string | null | undefined): string {
        const normalized = typeof value === "string" ? value.trim() : "";
        if (!normalized) {
            return "Unknown";
        }

        return normalized
            .split(/[._-]+/)
            .filter((segment) => segment.length > 0)
            .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
            .join(" ");
    }

    private initializeCatalogNavigation(): void {
        if (!this.route) {
            this.loadVocabularyCatalog(null, null, null);
            return;
        }

        this.route.queryParamMap
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((parameters) => {
                const queryKey = this.catalogQueryKey(Object.fromEntries(parameters.keys.map(key => [key, parameters.get(key)])));
                const internallyApproved = queryKey === this.approvedCatalogQuery;
                this.approvedCatalogQuery = null;
                const isConceptCatalog = parameters.get("mode") !== "editor"
                    && parameters.get("mode") !== "vocabulary-editor"
                    && !parameters.get("definition") && !parameters.get("token") && !parameters.get("configuration");
                const identityKeys = ["mode", "definition", "token", "configuration", "q", "kind", "status", "usageState", "page"];
                const editorSelectionChanged = this.lastEditorParameters !== null
                    && identityKeys.some(key => (this.lastEditorParameters?.[key] ?? null) !== parameters.get(key));
                if (!internallyApproved && !this.showConceptCatalog() && (isConceptCatalog || editorSelectionChanged)
                    && !this.confirmDiscardVocabularyChanges()) {
                    if (this.router && this.route && this.lastEditorParameters) {
                        void this.router.navigate([], { relativeTo: this.route, queryParams: this.lastEditorParameters, fragment: this.lastEditorFragment ?? undefined, replaceUrl: true });
                    }
                    return;
                }
                this.showConceptCatalog.set(isConceptCatalog);
                if (!internallyApproved && editorSelectionChanged) this.resetVocabularyDefinitionCreateState();
                if (!isConceptCatalog) {
                    this.lastEditorParameters = Object.fromEntries(parameters.keys.map(key => [key, parameters.get(key)]));
                    this.lastEditorFragment = this.route?.snapshot?.fragment ?? null;
                }
                this.catalogSearch = parameters.get("q")?.trim() ?? "";
                this.catalogKindCode = parameters.get("kind")?.trim() ?? "";
                this.catalogStatusCode = parameters.get("status")?.trim() ?? "";
                this.catalogUsageStateCode = parameters.get("usageState")?.trim() ?? "";
                this.catalogPage = this.readPositiveInteger(parameters.get("page")) ?? 1;
                this.selectedUsageId.set(parameters.get("usage")?.trim() || null);
                this.selectedVocabularyView.set(this.readVocabularyView(parameters.get("view")));
                this.contextualSource.set(this.readContextualSource(parameters));

                const mode = parameters.get("mode");
                const isLegacyEditor = mode === "editor";
                this.workspaceMode.set(isLegacyEditor ? "legacy-editor" : "catalog");
                if (isConceptCatalog) {
                    ++this.vocabularyCatalogRequestId;
                    ++this.definitionDetailRequestId;
                    this.vocabularyCatalog.set(null);
                    this.selectedDefinitionDetail.set(null);
                    this.clearChildEditingState();
                    this.resetVocabularyDefinitionCreateState();
                    this.isVocabularyCatalogLoading.set(false);
                    this.isDefinitionDetailLoading.set(false);
                    return;
                }
                if (isLegacyEditor) {
                    this.clearChildEditingState();
                    this.resetVocabularyDefinitionCreateState();
                    const entryId = this.readPositiveInteger(parameters.get("entry"));
                    if (entryId) {
                        if (this.selectedEntryId() !== entryId) {
                            this.selectEntry(entryId);
                        }
                    } else if (!this.isCreatingEntry()) {
                        this.beginCreateEntry(false);
                    }
                    return;
                }

                const requestedDefinitionId = this.readPositiveInteger(parameters.get("definition"));
                if (requestedDefinitionId
                    && requestedDefinitionId === this.pendingInlineDefinitionId
                    && this.vocabularyCatalog()) {
                    this.pendingInlineDefinitionId = null;
                    return;
                }
                this.pendingInlineDefinitionId = null;
                this.loadVocabularyCatalog(
                    requestedDefinitionId,
                    parameters.get("token"),
                    parameters.get("configuration"),
                );
            });
    }

    private loadVocabularyCatalog(
        requestedDefinitionId: number | null,
        requestedTokenCode: string | null,
        requestedConfigurationCode: string | null,
    ): void {
        this.isVocabularyCatalogLoading.set(true);
        this.vocabularyCatalogError.set(null);
        const requestId = ++this.vocabularyCatalogRequestId;
        const authority = this.editorAuthorityKey();
        const requestedToken = requestedTokenCode?.trim() ?? "";
        const requestedConfiguration = requestedConfigurationCode?.trim() ?? "";
        const requestedIdentitySearch = requestedDefinitionId === null
            ? requestedToken || requestedConfiguration
            : "";

        this.terminologyAdminService
            .getVocabularyCatalog({
                search: requestedIdentitySearch || this.catalogSearch.trim() || undefined,
                configurationCode: requestedConfiguration || undefined,
                kindCode: this.catalogKindCode || undefined,
                statusCode: this.catalogStatusCode || undefined,
                usageStateCode: this.catalogUsageStateCode || undefined,
                skip: (this.catalogPage - 1) * CATALOG_PAGE_SIZE,
                take: CATALOG_PAGE_SIZE,
            })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (catalog) => {
                    if (requestId !== this.vocabularyCatalogRequestId || authority !== this.editorAuthorityKey() || !this.canRead()) {
                        return;
                    }

                    this.vocabularyCatalog.set(catalog);
                    this.isVocabularyCatalogLoading.set(false);
                    const totalPages = Math.max(1, Math.ceil(catalog.totalCount / CATALOG_PAGE_SIZE));
                    if (catalog.totalCount > 0 && this.catalogPage > totalPages) {
                        this.goToCatalogPage(totalPages);
                        return;
                    }

                    const hasRequestedIdentity = requestedToken.length > 0 || requestedConfiguration.length > 0;
                    const requestedItem = requestedDefinitionId
                        ? catalog.items.find((item) => item.definitionId === requestedDefinitionId)
                        : catalog.items.find((item) =>
                            (requestedToken.length === 0
                                || normalizeCode(item.tokenCode) === normalizeCode(requestedToken))
                            && (requestedConfiguration.length === 0
                                || normalizeCode(item.configurationCode) === normalizeCode(requestedConfiguration)),
                        );
                    const selectedDefinitionId = requestedDefinitionId
                        ?? requestedItem?.definitionId
                        ?? (hasRequestedIdentity ? null : catalog.items[0]?.definitionId)
                        ?? null;

                    if (selectedDefinitionId) {
                        this.loadVocabularyDefinition(selectedDefinitionId);
                    } else {
                        this.definitionDetailRequestId += 1;
                        this.isDefinitionDetailLoading.set(false);
                        this.selectedDefinitionDetail.set(null);
                        this.clearChildEditingState();
                        if (hasRequestedIdentity) {
                            const requestedIdentity = [
                                requestedToken ? `token "${requestedToken}"` : null,
                                requestedConfiguration ? `configuration "${requestedConfiguration}"` : null,
                            ].filter((value): value is string => value !== null).join(" and ");
                            this.vocabularyCatalogError.set({
                                title: "Terminology definition not found",
                                messages: [`No versioned terminology definition matches ${requestedIdentity}.`],
                                issueCodes: [],
                            });
                        }
                    }
                },
                error: (error) => {
                    if (requestId !== this.vocabularyCatalogRequestId || authority !== this.editorAuthorityKey()) {
                        return;
                    }

                    this.isVocabularyCatalogLoading.set(false);
                    if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
                        this.onTerminologyAccessLost();
                    }
                    this.vocabularyCatalogError.set(mapTerminologyAdminError(error));
                },
            });
    }

    private vocabularyConfigurationId(detail: TerminologyVocabularyDefinitionDetail | null): number | null {
        return detail?.definition.configurationVersionId ?? detail?.definition.initialWording?.configurationVersionId ?? null;
    }

    private loadVocabularyDefinition(definitionId: number): void {
        this.isDefinitionDetailLoading.set(true);
        this.tokenCopied.set(false);
        const requestId = ++this.definitionDetailRequestId;
        const authority = this.editorAuthorityKey();
        const retainedDefinition = this.selectedDefinitionId() === definitionId ? this.selectedDefinitionDetail()?.definition : null;
        const retainedConfigurationId = retainedDefinition ? this.vocabularyConfigurationId(this.selectedDefinitionDetail()) : null;

        this.terminologyAdminService
            .getVocabularyDefinition(definitionId)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (detail) => {
                    if (requestId !== this.definitionDetailRequestId || authority !== this.editorAuthorityKey() || !this.canRead()) {
                        return;
                    }

                    if (detail.definition.definitionId !== definitionId) {
                        this.isDefinitionDetailLoading.set(false);
                        const error = new Error("The returned wording identity changed. Retry the selected definition.");
                        this.vocabularyEditor?.failCurrentWordingReload(error);
                        this.vocabularyCatalogError.set(mapTerminologyAdminError(error));
                        return;
                    }

                    const identityChanged = retainedDefinition && (retainedDefinition.tokenCode !== detail.definition.tokenCode
                        || retainedDefinition.configurationCode !== detail.definition.configurationCode);
                    if ((identityChanged || (retainedConfigurationId != null && this.vocabularyConfigurationId(detail) !== retainedConfigurationId))
                        && (this.childEditingState.dirty || this.vocabularyEditor?.hasUnsavedChanges)) {
                        this.isDefinitionDetailLoading.set(false);
                        const error = new HttpErrorResponse({ status: 409, error: {
                            title: "Editing context changed",
                            detail: "Your proposed wording is retained. The registered identity or current program pack changed; reopen Concepts explicitly before editing that different context.",
                        } });
                        this.vocabularyEditor?.failCurrentWordingReload(error);
                        this.vocabularyCatalogError.set(mapTerminologyAdminError(error));
                        return;
                    }
                    if (this.selectedDefinitionId() !== definitionId) this.clearChildEditingState();
                    this.selectedDefinitionDetail.set(detail);
                    this.vocabularyCatalog.update((catalog) => catalog
                        ? {
                            ...catalog,
                            items: catalog.items.map((item) => item.definitionId === detail.definition.definitionId
                                ? detail.definition
                                : item),
                        }
                        : catalog);
                    this.isDefinitionDetailLoading.set(false);
                    const usageId = this.selectedUsageId();
                    if (usageId && typeof document !== "undefined") {
                        window.setTimeout(() => {
                            document.getElementById(`terminology-usage-${usageId}`)?.scrollIntoView({ block: "nearest" });
                        });
                    }
                },
                error: (error) => {
                    if (requestId !== this.definitionDetailRequestId || authority !== this.editorAuthorityKey()) {
                        return;
                    }

                    this.isDefinitionDetailLoading.set(false);
                    if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
                        this.onTerminologyAccessLost();
                    } else this.vocabularyEditor?.failCurrentWordingReload(error);
                    this.vocabularyCatalogError.set(mapTerminologyAdminError(error));
                },
            });
    }

    private updateCatalogQuery(parameters: Params): void {
        if (!this.router || !this.route) {
            Object.entries(parameters).forEach(([key, value]) => {
                switch (key) {
                    case "q": this.catalogSearch = typeof value === "string" ? value : ""; break;
                    case "kind": this.catalogKindCode = typeof value === "string" ? value : ""; break;
                    case "status": this.catalogStatusCode = typeof value === "string" ? value : ""; break;
                    case "usageState": this.catalogUsageStateCode = typeof value === "string" ? value : ""; break;
                    case "usage": this.selectedUsageId.set(typeof value === "string" ? value : null); break;
                    case "view": this.selectedVocabularyView.set(this.readVocabularyView(typeof value === "string" ? value : null)); break;
                    case "page": this.catalogPage = typeof value === "number" ? value : 1; break;
                    default: break;
                }
            });
            if (parameters["mode"] === "editor") {
                this.workspaceMode.set("legacy-editor");
                const entryId = typeof parameters["entry"] === "number" ? parameters["entry"] : null;
                if (entryId) {
                    this.selectEntry(entryId);
                } else {
                    this.beginCreateEntry(false);
                }
            } else {
                this.workspaceMode.set("catalog");
                if (this.pendingInlineDefinitionId) {
                    this.pendingInlineDefinitionId = null;
                    return;
                }
                this.loadVocabularyCatalog(
                    typeof parameters["definition"] === "number" ? parameters["definition"] : null,
                    typeof parameters["token"] === "string" ? parameters["token"] : null,
                    typeof parameters["configuration"] === "string" ? parameters["configuration"] : null,
                );
            }
            return;
        }

        const approved = Object.fromEntries(this.route.snapshot.queryParamMap.keys.map(key => [key, this.route!.snapshot.queryParamMap.get(key)]));
        for (const [key, value] of Object.entries(parameters)) {
            if (value === null || value === undefined) delete approved[key];
            else approved[key] = String(value);
        }
        this.approvedCatalogQuery = this.catalogQueryKey(approved);
        void this.router.navigate([], {
            relativeTo: this.route,
            queryParams: parameters,
            queryParamsHandling: "merge",
            preserveFragment: true,
        }).then(success => { if (!success) this.approvedCatalogQuery = null; });
    }

    private catalogQueryKey(parameters: Params): string {
        return JSON.stringify(Object.keys(parameters).sort().map(key => [key, String(parameters[key] ?? "")]));
    }

    private readPositiveInteger(value: string | null): number | null {
        if (!value) {
            return null;
        }

        const parsed = Number.parseInt(value, 10);
        return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
    }

    private readContextualSource(parameters: ParamMap): TerminologyContextualSource | null {
        const source: TerminologyContextualSource = {
            returnUrl: normalizeTerminologyReturnUrl(parameters.get("returnUrl")),
            returnUsageId: normalizeTerminologyReturnUsage(parameters.get("returnUsage")),
            localeCode: this.readContextualValue(parameters.get("sourceLocale")),
            jurisdictionCode: this.readContextualValue(parameters.get("sourceJurisdiction")),
            programCode: this.readContextualValue(parameters.get("sourceProgram")),
            channelCode: this.readContextualValue(parameters.get("sourceChannel")),
            audienceCode: this.readContextualValue(parameters.get("sourceAudience")),
            mapRevision: this.readContextualValue(parameters.get("sourceRevision"), 128),
        };
        return Object.values(source).some(value => value !== null) ? source : null;
    }

    private readContextualValue(value: string | null, maximumLength = 120): string | null {
        return normalizeTerminologyContextValue(value, maximumLength);
    }

    private readVocabularyView(value: string | null): TerminologyEditorWorkspaceView {
        switch (value?.trim().toLowerCase()) {
            case "impact":
            case "history":
            case "technical":
                return value.trim().toLowerCase() as TerminologyEditorWorkspaceView;
            default:
                return "edit";
        }
    }

    private confirmDiscardVocabularyChanges(): boolean {
        if (this.editorState.busy() || this.vocabularyEditor?.busy) return false;
        const hasNewDefinitionChanges = this.isCreatingVocabularyDefinition()
            && [
                this.newDefinitionTokenCode,
                this.newDefinitionContentText,
                this.newDefinitionDescription,
            ].some(value => value.trim().length > 0);
        return (!this.vocabularyEditor?.hasUnsavedChanges && !hasNewDefinitionChanges)
            || typeof window === "undefined"
            || window.confirm("Discard unsaved wording changes and continue?");
    }

    private resetVocabularyDefinitionCreateState(): void {
        ++this.creationRequestId;
        this.isCreatingVocabularyDefinition.set(false);
        this.isCreatingVocabularyDefinitionSaving.set(false);
        this.newDefinitionTokenCode = "";
        this.newDefinitionContentText = "";
        this.newDefinitionDescription = "";
        this.newDefinitionFieldErrors.set({});
        this.syncEditorNavigationState();
    }

    private buildCatalogColumnDefs(): ColDef[] {
        return [
            { field: "tokenCode", headerName: "Token", flex: 1, minWidth: 120 },
            { field: "localeCode", headerName: "Locale", width: 90 },
            { field: "statusCode", headerName: "Status", width: 100, valueFormatter: ({ value }) => this.toStatusLabel(value as string) },
            {
                field: "effectiveStartUtc",
                headerName: "Eff. Start",
                width: 130,
                valueFormatter: ({ value }) => value ? new Date(value as string).toLocaleDateString() : "",
            },
            {
                headerName: "",
                width: 90,
                maxWidth: 90,
                sortable: false,
                cellRenderer: ButtonCellRenderer,
                cellRendererParams: {
                    label: "Select",
                    onClick: (params: { rowData: { id: number } }) => this.selectEntry(params.rowData.id),
                },
            },
        ];
    }

    private buildCatalogGridConfig(): EzGridDsConfig {
        return {
            datasourceId: "platformTerminologyEntries",
            bindings: [
                {
                    bindingType: "READ",
                    endpoint: this.terminologyAdminService.getODataEndpoint(),
                    protocol: "GET_ODATA",
                },
            ],
            colDefs: this.catalogColumnDefs,
        };
    }

    private buildCatalogPanelHeaderConfig(): PanelHeaderConfig {
        return {
            panelHeaderTitle: "Catalog",
            titleClickToReload: true,
            showStandardFilterControls: true,
            filterInputConfigs: [
                { type: "text", name: "configurationCode", label: "Configuration Code" },
                { type: "text", name: "versionCode", label: "Version Code" },
            ] as InputConfig[],
        };
    }

    private clearMessages(): void {
        this.successMessage.set(null);
        this.errorState.set(null);
    }

    private loadUsageManifest(): void {
        this.isUsageManifestLoading.set(true);
        this.usageManifestError.set(null);

        this.terminologyAdminService
            .getUsageManifest()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (manifest) => {
                    this.usageManifest.set(manifest);
                    this.isUsageManifestLoading.set(false);
                },
                error: () => {
                    this.isUsageManifestLoading.set(false);
                    this.usageManifestError.set(
                        "Usage evidence is temporarily unavailable. You can continue editing terminology entries.",
                    );
                },
            });
    }

    private ensureFormIsValid(form: EzFormComponent | undefined, fallbackMessage: string): boolean {
        const formGroup = form?.theFormGroup;
        if (!formGroup || formGroup.valid) {
            return true;
        }

        formGroup.markAllAsTouched();
        const controlErrors = form?.controlErrors?.map((entry) => entry.message) ?? [];
        this.errorState.set({
            title: "Validation failed",
            messages: controlErrors.length > 0 ? [...new Set(controlErrors)] : [fallbackMessage],
            issueCodes: [],
        });

        return false;
    }

    private buildTerminologyFormConfig(isCreateMode: boolean): InputConfig[] {
        return [
            {
                name: "configurationCode",
                type: EzFormInputType.TEXT,
                label: "Configuration Code",
                row: 1,
                width: "1x",
                readOnly: !isCreateMode,
                validation: isCreateMode ? [Validators.required] : [],
                validationMessages: {
                    required: "Configuration code is required.",
                },
            },
            {
                name: "versionCode",
                type: EzFormInputType.TEXT,
                label: "Version Code",
                row: 1,
                width: "1x",
                readOnly: !isCreateMode,
                validation: isCreateMode ? [Validators.required] : [],
                validationMessages: {
                    required: "Version code is required.",
                },
            },
            {
                name: "tokenCode",
                type: EzFormInputType.TEXT,
                label: "Token Code",
                row: 2,
                width: "1x",
                readOnly: !isCreateMode,
                validation: isCreateMode ? [Validators.required] : [],
                validationMessages: {
                    required: "Token code is required.",
                },
            },
            {
                name: "localeCode",
                type: EzFormInputType.TEXT,
                label: "Locale Code",
                row: 2,
                width: "1x",
                validation: [Validators.required],
                validationMessages: {
                    required: "Locale code is required.",
                },
            },
            {
                name: "channelCode",
                type: EzFormInputType.TEXT,
                label: "Channel Code",
                row: 3,
                width: "1x",
            },
            {
                name: "audienceCode",
                type: EzFormInputType.TEXT,
                label: "Audience Code",
                row: 3,
                width: "1x",
            },
            {
                name: "tenantId",
                type: EzFormInputType.TEXT,
                label: "Tenant Id",
                row: 4,
                width: "1x",
            },
            {
                name: "effectiveStartUtc",
                type: EzFormInputType.DATE_TIME,
                label: "Effective Start (Local Time)",
                row: 4,
                width: "1x",
                customTooltip: "Displayed in local time and saved back to the API as UTC.",
                validation: [Validators.required],
                validationMessages: {
                    required: "Effective start is required.",
                },
                dateTimeConfig: {
                    hourFormat: "24",
                    showSeconds: false,
                    showOnFocus: false,
                    showClear: false,
                },
            },
            {
                name: "effectiveEndUtc",
                type: EzFormInputType.DATE_TIME,
                label: "Effective End (Local Time)",
                row: 5,
                width: "1x",
                customTooltip: "Leave blank for an open-ended terminology window.",
                dateTimeConfig: {
                    hourFormat: "24",
                    showSeconds: false,
                    showOnFocus: false,
                    showClear: true,
                },
            },
            {
                name: "defaultText",
                type: EzFormInputType.TEXTAREA,
                label: "Default Text",
                row: 6,
                width: "fill",
                textAreaRows: 4,
                validation: [Validators.required],
                validationMessages: {
                    required: "Default text is required.",
                },
            },
        ];
    }

    private createEmptyFormValues(): TerminologyEntryFormValues {
        return {
            configurationCode: "",
            versionCode: "",
            tokenCode: "",
            defaultText: "",
            localeCode: "en-us",
            channelCode: "web",
            audienceCode: "operator",
            tenantId: "",
            effectiveStartUtc: new Date(),
            effectiveEndUtc: null,
        };
    }

    private mapEntryToFormValues(entry: TerminologyEntryDetail): TerminologyEntryFormValues {
        return {
            configurationCode: entry.configurationCode,
            versionCode: entry.versionCode,
            tokenCode: entry.tokenCode,
            defaultText: entry.defaultText ?? "",
            localeCode: entry.localeCode ?? "",
            channelCode: entry.channelCode ?? "",
            audienceCode: entry.audienceCode ?? "",
            tenantId: entry.tenantId ?? "",
            effectiveStartUtc: entry.effectiveStartUtc ? new Date(entry.effectiveStartUtc) : null,
            effectiveEndUtc: entry.effectiveEndUtc ? new Date(entry.effectiveEndUtc) : null,
        };
    }

    private buildCreateRequest(
        values: TerminologyEntryFormValues,
        effectiveStartUtc: string,
        effectiveEndUtc: string | null,
    ): TerminologyEntryCreateRequest {
        return {
            configurationCode: values.configurationCode.trim(),
            versionCode: values.versionCode.trim(),
            tokenCode: values.tokenCode.trim(),
            defaultText: values.defaultText.trim(),
            localeCode: values.localeCode.trim(),
            channelCode: values.channelCode.trim() || null,
            audienceCode: values.audienceCode.trim() || null,
            tenantId: values.tenantId.trim() || null,
            effectiveStartUtc,
            effectiveEndUtc,
        };
    }

    private buildUpdateRequest(
        values: TerminologyEntryFormValues,
        effectiveStartUtc: string,
        effectiveEndUtc: string | null,
    ): TerminologyEntryUpdateRequest {
        return {
            defaultText: values.defaultText.trim(),
            localeCode: values.localeCode.trim(),
            channelCode: values.channelCode.trim() || null,
            audienceCode: values.audienceCode.trim() || null,
            tenantId: values.tenantId.trim() || null,
            effectiveStartUtc,
            effectiveEndUtc,
        };
    }

    private validateFormValues(
        values: TerminologyEntryFormValues,
        effectiveStartUtc: string | null,
        effectiveEndUtc: string | null,
    ): string[] {
        const messages: string[] = [];

        if (!effectiveStartUtc) {
            messages.push("Effective start must be a valid date and time.");
        }

        if (hasDateValue(values.effectiveEndUtc) && !effectiveEndUtc) {
            messages.push("Effective end must be a valid date and time.");
        }

        if (effectiveEndUtc && effectiveStartUtc && new Date(effectiveEndUtc).getTime() <= new Date(effectiveStartUtc).getTime()) {
            messages.push("Effective end must be later than effective start.");
        }

        return [...new Set(messages)];
    }

    private normalizeEntryDates(values: TerminologyEntryFormValues): {
        effectiveStartUtc: string | null;
        effectiveEndUtc: string | null;
    } {
        return {
            effectiveStartUtc: this.toIsoUtc(values.effectiveStartUtc),
            effectiveEndUtc: this.toIsoUtc(values.effectiveEndUtc),
        };
    }

    private toIsoUtc(value: string | Date | null): string | null {
        if (!value) {
            return null;
        }

        const candidate = value instanceof Date ? value : new Date(value);
        return Number.isNaN(candidate.getTime()) ? null : candidate.toISOString();
    }
}

function normalizeCode(value: string | null | undefined): string {
    return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function hasDateValue(value: string | Date | null): boolean {
    if (!value) {
        return false;
    }

    if (value instanceof Date) {
        return !Number.isNaN(value.getTime());
    }

    return value.trim().length > 0;
}
